import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { DialogService, GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { Event } from '@interfaces';
import { DomainDataService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface SessionRow {
	accSessionId: number,
	eventId: number,
	event: Event,
	topicNames: string,
	specialists: string,
	ventureAttendancePercent: number | undefined,
	surveyCompletionPercentage: number | undefined,
	sessionSurveyResults: number | undefined,
	sessionSurveyAccessibilityScore: number | undefined,
	sessionSurveyCommunicationScore: number | undefined,
	sessionSurveyOrganizationScore: number | undefined,
	updatedUTC: number,
}

@Component({
	selector: 'acc-sessions-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './sessions-view.part.html',
})
export class AccSessionsViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);
	private readonly domain = inject(DomainDataService);
	private readonly dialog = inject(DialogService);

	public setup = getSetup(this.util, this.dialog, this.areaService);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public sessions = computed<SessionRow[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];


		const events = this.areaService.data()?.events ?? [];

		const ventures = data.ventures;
		const sessions = data.sessions;
		const sessionTopics = data.sessionTopics;
		const accTopics = data.accTopics;
		const topicSpecialists = data.topicSpecialists;
		const sessionAttendees = data.sessionAttendees;
		const sessionSurveyResults = data.sessionSurveyResponses;

		const ddTopics = this.domain.data().topics;
		const ddTopicMap = this.util.array.toMap(ddTopics, topic => topic.topicId);

		const ventureNotDroppedOut = ventures.filter(team => team.status != 'Dropped Out' && team.status != 'Never Started');

		return sessions.map(session => {

			const event = events.find(e => e.eventId == session.eventId)!;


			//
			// Get the attendee records for this session
			//
			const attendees = sessionAttendees.filter(a => a.accSessionId == session.accSessionId && !!a.accVentureId);
			const attendeesPresent = attendees.filter(a => a.status == 'Present');


			//
			// Get the list of accVentureIds for this session. At a minimum, we include all ventures that haven't dropped out.
			// Next, we include accVentureIds of attendee records. This will bump up the count for past sessions when a team
			// drops out later. If there is an attendance record for a dropped out team then they hadn't dropped out yet.
			//
			const accVentureIds = this.util.array.cleanNumericIds([
				...ventureNotDroppedOut.map(v => v.accVentureId),
				...attendees.map(a => a.accVentureId!),
			]);

			const accVentureIdsPresent = this.util.array.cleanNumericIds([
				...attendeesPresent.map(a => a.accVentureId!)
			]);


			//
			// If we have at least one attendee record, either Absent or Presnet, then we will calculate a percentage of
			// the ventures that had at least one entrepreneur present for the session.
			//
			let ventureAttendancePercent: number | undefined = undefined;
			if (attendees.length) ventureAttendancePercent = accVentureIds.length == 0 ? 0 : Math.round((accVentureIdsPresent.length / accVentureIds.length) * 100);


			//
			// Topics and Specialists
			//
			const accSessionTopics = sessionTopics.filter(sessionTopic => sessionTopic.accSessionId == session.accSessionId);
			const accTopicIds = accSessionTopics.map(topic => topic.accTopicId);

			const sessionTopicIds = accTopics.filter(topic => accTopicIds.includes(topic.accTopicId)).map(topic => topic.topicId);
			const needsSpecialist = !!sessionTopicIds.map(topicId => ddTopicMap[topicId]).find(topic => topic?.needsSpecialist);

			let specialistNames = 'n/a';
			if (needsSpecialist) {
				specialistNames = topicSpecialists.filter(ts => accTopicIds.includes(ts.accTopicId)).map(ts => ts.personName).sort((a, b) => a > b ? 1 : -1).join(', ');
			}


			//
			// Survey results
			//
			const accSessionAttendeeIds = attendees.map(a => a.accSessionAttendeeId);
			const countOfAttendeesThatCompletedSurvey = sessionSurveyResults.filter(survey => survey.accSessionAttendeeId && accSessionAttendeeIds.includes(survey.accSessionAttendeeId)).length;

			let surveyCompletionPercentage = undefined;
			if (attendees.length) surveyCompletionPercentage = attendeesPresent.length ? Math.round(countOfAttendeesThatCompletedSurvey * 100 / attendeesPresent.length) : 0;


			return {
				eventId: session.eventId,
				event,
				accSessionId: session.accSessionId,
				topicNames: sessionTopicIds.length ? ddTopics.filter(topic => sessionTopicIds.includes(topic.topicId)).map(topic => topic.name).sort((a, b) => a > b ? 1 : -1).join(', ') : '',
				specialists: specialistNames,
				ventureAttendancePercent,
				surveyCompletionPercentage,
				sessionSurveyResults: countOfAttendeesThatCompletedSurvey || undefined,
				sessionSurveyAccessibilityScore: this.util.nps.calcScore(sessionSurveyResults.map(result => result.accessibility)) ?? undefined,
				sessionSurveyCommunicationScore: this.util.nps.calcScore(sessionSurveyResults.map(result => result.communication)) ?? undefined,
				sessionSurveyOrganizationScore: this.util.nps.calcScore(sessionSurveyResults.map(result => result.organization)) ?? undefined,
				updatedUTC: data.asOfUTC
			};
		});

	});
}